<template>
  <b>
    <a href="https://www.jakewelch.design" target="_blank" id="portfolioLink">
      jakewelch.design
    </a>
  </b>
  <div id="app">
    <div class="buttons">
      <button
        v-if="!showGUI"
        class="randomize-button main-random"
        @click="handleRandomize"
      >
        Randomize <span class="dice">🎲</span>
      </button>

      <!-- Button to toggle the GUI -->
      <button
        class="toggle-button"
        :class="{ 'toggle-button-open': showGUI }"
        @click="toggleGUI"
      >
        {{ showGUI ? 'Close menu' : 'View archive' }}
      </button>
    </div>

    <!-- GUI Component -->
    <GUI
      :restrooms="restrooms"
      :selectedRestroom="selectedRestroom"
      :visible="showGUI"
      @update:selectedRestroom="onRestroomChange"
      @randomize="handleRandomize"
    />

    <!-- Pass the selected restroom to the Viewport -->
    <Viewport v-if="selectedRestroom" :restroom="selectedRestroom" />
  </div>
  <div class="footer-container">
    <Footer />
  </div>
</template>

<script>
  import Viewport from './components/Viewport.vue';
  import GUI from './components/GUI.vue';
  import restroomData from './data/restroom-data.js';
  import Footer from './components/Footer.vue';

  export default {
    name: 'App',
    components: {
      Viewport,
      GUI,
      Footer,
    },
    data() {
      return {
        restrooms: restroomData,
        selectedRestroom: null,
        showGUI: false,
      };
    },
    watch: {
      '$route.params.slug'(newSlug) {
        this.handleRouteChange(newSlug);
      },
    },
    methods: {
      handleRouteChange(slug) {
        if (!slug) {
          // No slug: randomize
          const randomRestroom = this.getRandomRestroom();
          this.selectedRestroom = randomRestroom;
          if (this.$route.params.slug !== randomRestroom.slug) {
            this.$router.replace({ path: `/${randomRestroom.slug}` });
          }
        } else {
          // Slug present: always load that restroom
          const match = this.restrooms.find((r) => r.slug === slug);
          if (match) {
            this.selectedRestroom = match;
          } else {
            this.selectedRestroom = null;
          }
        }
      },
      getRandomRestroom() {
        const randomIndex = Math.floor(Math.random() * this.restrooms.length);
        return this.restrooms[randomIndex];
      },
      onRestroomChange(newRestroom) {
        this.selectedRestroom = newRestroom;
        if (newRestroom && newRestroom.slug !== this.$route.params.slug) {
          this.$router.push({ path: `/${newRestroom.slug}` });
        }
      },
      toggleGUI() {
        this.showGUI = !this.showGUI;
      },
      handleRandomize() {
        const restroom = this.getRandomRestroom();
        this.selectedRestroom = restroom;
        this.$router.push({ path: `/${restroom.slug}` });
      },
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        vm.handleRouteChange(to.params.slug);
      });
    },
  };
</script>

<style>
  ::-moz-selection {
    /* Code for Firefox */
    color: rgb(255, 255, 255);
    background: rgb(255, 0, 0);
  }

  ::selection {
    color: rgb(255, 255, 255);
    background: rgb(255, 0, 0);
  }

  #portfolioLink {
    position: absolute;
    margin: 0;
    padding: 15px;
    right: 0;
    top: 0;
    line-height: 0.5;
    font-family: satoshi;
    font-size: 18px;
    color: var(--color-2-solid);
    text-decoration: none;
    text-shadow: 0 4px 30px rgba(0, 0, 0, 1);
    z-index: 1000;
  }

  #portfolioLink:hover {
    text-decoration: none;
    color: red;
  }

  .buttons {
    position: fixed;
    top: 45px;
    left: 15px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px; /* Add spacing between buttons */
  }

  .toggle-button {
    font-family: satoshi;
    z-index: 101;

    background: rgba(0, 0, 0, 0);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(6.5px);
    -webkit-backdrop-filter: blur(6.5px);
    color: var(--color-2-solid);
    border: 1px solid rgba(255, 255, 255, 0.4);
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

    border-radius: 5px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    padding: 3px 10px;
    cursor: pointer;
    font-size: 14px;
    width: 160px;
  }

  .toggle-button:hover {
    background: rgba(25, 25, 25, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.43);
    color: var(--color-2-solid);
  }

  .toggle-button-open {
    background: var(--color-2-transparent);
    color: var(--color-1-solid);
    border: none;
    width: 290px;
  }

  .randomize-button.main-random {
    font-family: satoshi;
    font-size: 14px;
    padding: 3px 10px;
    background: rgba(0, 0, 0, 0);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(6.5px);
    -webkit-backdrop-filter: blur(6.5px);
    color: var(--color-2-solid);
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 5px;
    cursor: pointer;
    width: 160px;
    /* Remove margin-left, margin-top, position, left, z-index */
  }
  .randomize-button.main-random:hover {
    background: rgba(25, 25, 25, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.43);
    color: var(--color-2-solid);
  }

  .randomize-button.main-random:active .dice {
    transform: rotate(360deg);
  }

  .dice {
    display: inline-block;
    transition: transform 0.4s;
  }

  .footer-container {
    position: fixed;
    z-index: 1000;
    bottom: 0;
    width: 100%;
  }

  @media (max-width: 600px) {
    .toggle-button,
    .randomize-button.main-random {
      font-size: 8px;
      width: 25vw;
    }

    #portfolioLink {
      font-size: 14px;
      text-shadow: none;
    }
  }
</style>
